import { cn } from '@tempo-io/sui-class-utils';
import { useCallback, useEffect, useRef, useState } from 'react';
import {
  bindCardSelectionDisabled,
  useSwimlaneRootProps,
} from '@/common/components/ui/swimlane/context';
import {
  RESIZE_HANDLE_HITBOX_PADDING,
  RESIZE_HANDLE_WIDTH,
  ROW_HEADER_WIDTH,
} from '@/common/components/ui/swimlane/layout/constants';
import { useResizableHandle } from '@/common/hooks/useResizableHandle';

const MIN_WIDTH = ROW_HEADER_WIDTH.horizontal.value;
const MAX_WIDTH = 600;

interface ISwimlaneRowHeaderResize {
  rowHeaderWidth: number;
  groupColumnOffset: number;
  offset: number;
  rowHeadersContainer: HTMLDivElement | null;
  scrollContainer: HTMLElement | null;
}

export function SwimlaneRowHeaderResize({
  rowHeaderWidth,
  groupColumnOffset,
  offset,
  rowHeadersContainer,
  scrollContainer,
}: ISwimlaneRowHeaderResize) {
  const { rowHeaderOrientation, readOnly, actions } = useSwimlaneRootProps();

  const handleWidthChange = (width: number) => {
    actions?.headerActions.setRowHeaderWidth?.(width - groupColumnOffset);
  };

  const { currentWidth, isDragging, handleRef, containerProps } = useResizableHandle({
    initialWidth: rowHeaderWidth,
    minWidth: MIN_WIDTH,
    maxWidth: MAX_WIDTH,
    onWidthChange: handleWidthChange,
    enabled: !readOnly && rowHeaderOrientation !== 'vertical',
  });

  const rowHeadersContainerRef = useRef<HTMLDivElement | null>(rowHeadersContainer);
  const scrollContainerRef = useRef<HTMLElement | null>(scrollContainer);
  const [containerTop, setContainerTop] = useState(0);
  const [containerHeight, setContainerHeight] = useState(0);
  const isDraggingRef = useRef(false);

  const updateContainerPosition = useCallback(() => {
    if (isDraggingRef.current) return;
    const rowHeadersEl = rowHeadersContainerRef.current;
    const scrollEl = scrollContainerRef.current;
    if (!rowHeadersEl || !scrollEl) return;

    const rowHeadersRect = rowHeadersEl.getBoundingClientRect();
    const scrollRect = scrollEl.getBoundingClientRect();

    const top = rowHeadersRect.top || 0;
    const bottom = scrollRect.bottom || 0;
    setContainerTop(top);
    setContainerHeight(bottom - top);
  }, []);

  useEffect(() => {
    rowHeadersContainerRef.current = rowHeadersContainer;
    scrollContainerRef.current = scrollContainer;
    updateContainerPosition();
  }, [rowHeadersContainer, scrollContainer, updateContainerPosition]);

  useEffect(() => {
    const rowHeadersEl = rowHeadersContainerRef.current;
    const scrollEl = scrollContainerRef.current;
    if (!rowHeadersEl || !scrollEl) return;

    const resizeObserver = new ResizeObserver(() => updateContainerPosition());
    resizeObserver.observe(rowHeadersEl);
    resizeObserver.observe(scrollEl);
    window.addEventListener('resize', updateContainerPosition);

    return () => {
      resizeObserver.disconnect();
      window.removeEventListener('resize', updateContainerPosition);
    };
  }, [updateContainerPosition]);

  useEffect(() => {
    isDraggingRef.current = isDragging;
  }, [isDragging]);

  if (readOnly) return null;
  if (rowHeaderOrientation === 'vertical') return null;

  const handlePosition = currentWidth + offset;

  return (
    <div
      data-testid="resize-container"
      {...containerProps}
      style={{
        cursor: 'col-resize',
        position: 'fixed',
        top: `${containerTop}px`,
        height: `${containerHeight}px`,
        zIndex: 100,
        pointerEvents: 'auto',
        ...(isDragging
          ? { left: 0, right: 0 }
          : {
              left: `${handlePosition - RESIZE_HANDLE_HITBOX_PADDING.value}px`,
              width: `${RESIZE_HANDLE_HITBOX_PADDING.value * 2}px`,
            }),
      }}
    >
      <div
        ref={handleRef}
        role="slider"
        tabIndex={0}
        aria-valuenow={currentWidth}
        aria-label="Adjust the Row Label Width"
        data-dragging={isDragging}
        className={cn(
          'fixed z-100 pointer-events-auto transition-opacity duration-120',
          'opacity-0 hover:opacity-100 data-[dragging=true]:opacity-100',
          'hover:bg-bg-neutral-bold data-[dragging=true]:bg-bg-neutral-bold/80',
        )}
        style={{
          left: `${handlePosition}px`,
          top: `${containerTop}px`,
          height: `${containerHeight}px`,
          width: `${RESIZE_HANDLE_WIDTH.value}px`,
        }}
        {...bindCardSelectionDisabled()}
      />
    </div>
  );
}
